import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import type { AgentChatMessage, AgentChatSession } from '~/api/agentChat'
import {
  deleteSession as deleteSessionAPI,
  getSession,
  getUserSessions,
} from '~/api/agentChat'
import { useAgentChatAPI } from '~/hooks/useAgentChatAPI'
import { useAgentChatSync } from '~/hooks/useAgentChatSync'
import { useIsAgentChatEnabled } from '~/hooks/useIsAgentChatEnabled'
import { getHasOpenDialogs } from '~/store/duck/dialogs'
import {
  getCurrentBusiness,
  getCurrentBusinessId,
  getCurrentBusinessTimeZone,
  getCurrentUser,
  getCurrentUserId,
} from '~/store/reducers/auth'
import { isFirstOpenToday, markFirstOpenDone } from '~/utils/agentChatFirstOpen'
import {
  InputMethod,
  trackEvent,
  TrackingEvent,
} from '~/utils/trackingProvider'

import type { FloatingPosition, FloatingSize } from './types'
import { ChatMode } from './types'

export { ChatMode } from './types'
export type { FloatingPosition, FloatingSize } from './types'

export enum ActionError {
  FAILED_TO_LOAD_SESSIONS = 'FAILED_TO_LOAD_SESSIONS',
  FAILED_TO_SELECT_SESSION = 'FAILED_TO_SELECT_SESSION',
  FAILED_TO_CREATE_SESSION = 'FAILED_TO_CREATE_SESSION',
  FAILED_TO_DELETE_SESSION = 'FAILED_TO_DELETE_SESSION',
  FAILED_TO_LOAD_SESSION = 'FAILED_TO_LOAD_SESSION',
}

const INITIAL_FLOATING_POSITION_Y = 72 // Below the header
const INITIAL_FLOATING_WIDTH = 600
const INITIAL_FLOATING_HEIGHT = 500
const SCROLLBAR_WIDTH = 16

interface ChatInputState {
  message: string
  messageCreatedViaVoice: boolean
  selectedFile: File | null
}

interface AgentChatContextType {
  actionError: ActionError | null
  chatMode: ChatMode
  clearInput: () => void
  clearMessages: () => void
  closeChat: () => void
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error'
  createNewSession: () => Promise<void>
  deleteSession: (sessionId: string) => Promise<boolean>
  dismissActionError: () => void
  error: string | null
  floatingPosition: FloatingPosition
  floatingSize: FloatingSize
  inputState: ChatInputState
  isChatOpen: boolean
  isLoading: boolean
  isLoadingSessions: boolean
  isResponding: boolean
  isRespondingInAnotherTab: boolean
  loadSessions: () => Promise<void>
  messages: AgentChatMessage[]
  openChat: () => void
  selectSession: (session: AgentChatSession) => Promise<void>
  sendMessage: (params: {
    content: string
    file?: { data: ArrayBuffer; name: string; type: string }
    inputMethod?: InputMethod
    promptMetadata?: Record<string, any>
    stateDelta?: Record<string, any>
  }) => Promise<void>
  session: AgentChatSession | null
  sessions: AgentChatSession[]
  setChatMode: (mode: ChatMode) => void
  setInputState: (
    state: ChatInputState | ((prev: ChatInputState) => ChatInputState),
  ) => void
  startSession: () => Promise<void>
  streamingFinalStarted: boolean
  streamingIntermediateText: string | null
  toggleChatMode: () => void
  updateFloatingPosition: (position: FloatingPosition) => void
  updateFloatingSize: (size: FloatingSize) => void
}

export const AgentChatContext = createContext<AgentChatContextType | undefined>(
  undefined,
)

// Separate stable context for isChatOpen and chatMode that doesn't include messages
// This prevents components like App from re-rendering when messages change
interface AgentChatStateContextType {
  chatMode: ChatMode
  isChatOpen: boolean
}

export const AgentChatStateContext = createContext<
  AgentChatStateContextType | undefined
>(undefined)

interface AgentChatProviderProps {
  children: ReactNode
}

export function AgentChatProvider({ children }: AgentChatProviderProps) {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [chatMode, setChatMode] = useState<ChatMode>(ChatMode.FLOATING)
  const [sessions, setSessions] = useState<AgentChatSession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [sessionOpenedAt, setSessionOpenedAt] = useState<number | null>(null)
  const [actionError, setActionError] = useState<ActionError | null>(null)
  const [isRespondingInAnotherTab, setIsRespondingInAnotherTab] =
    useState(false)
  const [floatingPosition, setFloatingPosition] = useState<FloatingPosition>({
    x:
      typeof window !== 'undefined'
        ? window.innerWidth - INITIAL_FLOATING_WIDTH - SCROLLBAR_WIDTH
        : 400,
    y: INITIAL_FLOATING_POSITION_Y,
  })
  const [floatingSize, setFloatingSize] = useState<FloatingSize>({
    width: INITIAL_FLOATING_WIDTH,
    height:
      typeof window !== 'undefined'
        ? window.innerHeight - INITIAL_FLOATING_POSITION_Y * 2
        : INITIAL_FLOATING_HEIGHT,
  })

  const [inputState, setInputState] = useState<ChatInputState>({
    message: '',
    selectedFile: null,
    messageCreatedViaVoice: false,
  })

  const isAgentChatEnabled = useIsAgentChatEnabled()
  const currentUserId = useSelector(getCurrentUserId)
  const currentUser = useSelector(getCurrentUser)
  const currentBusinessId = useSelector(getCurrentBusinessId)
  const currentBusinessName = useSelector(getCurrentBusiness)?.name
  const currentBusinessTimezone = useSelector(getCurrentBusinessTimeZone)
  const isModalOpen = useSelector(getHasOpenDialogs)
  const previousBusinessIdRef = useRef<string | undefined>(currentBusinessId)

  const updateSession = useCallback((updatedSession: AgentChatSession) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === updatedSession.id ? updatedSession : s)),
    )
  }, [])

  const addSession = useCallback((newSession: AgentChatSession) => {
    setSessions((prev) => [newSession, ...prev])
  }, [])

  const {
    messages,
    loading: isLoading,
    isResponding,
    connectionState,
    sendMessage: hookSendMessage,
    startSession: hookStartSession,
    clearMessages,
    error,
    loadSession: hookLoadSession,
    session,
    streamingFinalStarted,
    streamingIntermediateText,
    updateMessagesFromSession,
  } = useAgentChatAPI({
    onSessionUpdate: updateSession,
    onSessionCreated: addSession,
  })

  const sessionIdRef = useRef<string | null>(null)
  const sessionOpenedAtRef = useRef<number | null>(null)
  const chatModeRef = useRef<ChatMode>(ChatMode.FLOATING)

  useEffect(() => {
    sessionIdRef.current = session?.id ?? null
  }, [session?.id])

  useEffect(() => {
    sessionOpenedAtRef.current = sessionOpenedAt
  }, [sessionOpenedAt])

  useEffect(() => {
    chatModeRef.current = chatMode
  }, [chatMode])

  // Wrapper function that updates both state and ref synchronously
  // This ensures the ref is current when openChat tracks analytics
  const setChatModeWithRef = useCallback(
    (mode: ChatMode | ((prev: ChatMode) => ChatMode)) => {
      if (typeof mode === 'function') {
        setChatMode((prev) => {
          const newMode = mode(prev)
          chatModeRef.current = newMode
          return newMode
        })
      } else {
        chatModeRef.current = mode
        setChatMode(mode)
      }
    },
    [],
  )

  const syncSessionFromAnotherTab = useCallback(
    async (sessionId: string) => {
      if (!currentUserId || !currentBusinessId) return

      const capturedSessionId = session?.id

      try {
        const updatedSession = await getSession(
          currentUserId,
          sessionId,
          currentBusinessId,
        )
        updateSession(updatedSession)

        if (capturedSessionId === sessionId && session?.id === sessionId) {
          updateMessagesFromSession(updatedSession)
        }
      } catch (caughtError) {
        // Silently fail - the other tab has the updated state
        // eslint-disable-next-line no-console
        console.warn('Failed to sync session from another tab:', caughtError)
      }
    },
    [
      currentUserId,
      currentBusinessId,
      session?.id,
      updateSession,
      updateMessagesFromSession,
    ],
  )

  const handleSessionUpdateFromAnotherTab = useCallback(
    async (sessionId: string) => {
      // Small delay to allow backend to persist the message
      await new Promise((resolve) => {
        setTimeout(resolve, 100)
      })

      await syncSessionFromAnotherTab(sessionId)
    },
    [syncSessionFromAnotherTab],
  )

  const handleIsRespondingUpdateFromAnotherTab = useCallback(
    async (responding: boolean, sessionId: string) => {
      setIsRespondingInAnotherTab(responding)

      // When another tab finishes responding, refetch to get the completed message
      if (!responding && session?.id === sessionId) {
        await syncSessionFromAnotherTab(sessionId)
      }
    },
    [session?.id, syncSessionFromAnotherTab],
  )

  // Sync chat state across browser tabs
  useAgentChatSync({
    session,
    messages,
    isResponding,
    onSessionUpdate: handleSessionUpdateFromAnotherTab,
    onIsRespondingUpdate: handleIsRespondingUpdateFromAnotherTab,
  })

  useEffect(() => {
    setIsRespondingInAnotherTab(false)
  }, [session?.id])

  const openChat = useCallback(() => {
    setIsChatOpen(true)
    setSessionOpenedAt(Date.now())
    const firstOpenOfDay = isFirstOpenToday(
      currentUserId ?? '',
      currentBusinessId ?? '',
      currentBusinessTimezone ?? null,
    )
    trackEvent(TrackingEvent.AI_ASSISTANT_OPENED, {
      chat_mode: chatModeRef.current,
      agent_session_id: session?.id ?? null,
      first_open_of_day: firstOpenOfDay,
    })
  }, [currentUserId, currentBusinessId, currentBusinessTimezone, session?.id])

  const closeChat = useCallback(() => {
    setIsChatOpen(false)

    const sessionDuration =
      sessionOpenedAt !== null ? Date.now() - sessionOpenedAt : null
    const messageCount = messages
      ? messages.filter((msg) => !msg.isLoading).length
      : 0

    trackEvent(TrackingEvent.AI_ASSISTANT_CLOSED, {
      chat_mode: chatMode,
      session_window_open_duration_ms: sessionDuration,
      message_count: messageCount,
      agent_session_id: session?.id ?? null,
    })

    setSessionOpenedAt(null)
  }, [chatMode, messages, session?.id, sessionOpenedAt])

  const updateFloatingPosition = (position: FloatingPosition) => {
    setFloatingPosition(position)
  }

  const updateFloatingSize = (size: FloatingSize) => {
    setFloatingSize(size)
  }

  const toggleChatMode = useCallback(() => {
    setChatModeWithRef((prev) =>
      prev === ChatMode.RAIL ? ChatMode.FLOATING : ChatMode.RAIL,
    )
  }, [])

  const dismissActionError = () => {
    setActionError(null)
  }

  const clearInput = useCallback(() => {
    setInputState({
      message: '',
      selectedFile: null,
      messageCreatedViaVoice: false,
    })
  }, [])

  const loadSessions = useCallback(async () => {
    if (!currentUserId || !currentBusinessId) return

    try {
      setIsLoadingSessions(true)
      const userSessions = await getUserSessions(
        currentUserId,
        currentBusinessId,
      )
      setSessions(userSessions)
      trackEvent(TrackingEvent.AI_ASSISTANT_SESSIONS_LOADED, {
        chat_session_count: userSessions.length,
      })
    } catch (caughtError) {
      setActionError(ActionError.FAILED_TO_LOAD_SESSIONS)
    } finally {
      setIsLoadingSessions(false)
    }
  }, [currentUserId, currentBusinessId])

  const selectSession = useCallback(
    async (selectedSession: AgentChatSession) => {
      if (!currentBusinessId) return

      const previousSessionId = session?.id ?? null
      const messageCountInPrevious = messages
        ? messages.filter((msg) => !msg.isLoading).length
        : 0

      try {
        await hookLoadSession(selectedSession.id, currentBusinessId)

        trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_SWITCHED, {
          previous_agent_session_id: previousSessionId,
          new_agent_session_id: selectedSession.id,
          message_count_in_previous: messageCountInPrevious,
          chat_mode: chatMode,
        })
      } catch (caughtError) {
        setActionError(ActionError.FAILED_TO_SELECT_SESSION)
      }
    },
    [currentBusinessId, hookLoadSession, session?.id, messages, chatMode],
  )

  const createNewSession = useCallback(async () => {
    const isFirstSession = sessions ? sessions.length === 0 : true

    try {
      const stateDelta = {
        business: {
          id: currentBusinessId,
          name: currentBusinessName,
          timezone: currentBusinessTimezone,
        },
        user: {
          id: currentUserId,
          email: currentUser?.email,
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
        },
      }

      await hookStartSession(stateDelta)

      trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_CREATED, {
        is_first_session: isFirstSession,
        total_sessions: sessions ? sessions.length + 1 : 1,
        chat_mode: chatMode,
        is_auto_created: false,
      })
    } catch (caughtError) {
      setActionError(ActionError.FAILED_TO_CREATE_SESSION)
    }
  }, [
    hookStartSession,
    sessions,
    chatMode,
    currentBusinessId,
    currentBusinessName,
    currentBusinessTimezone,
    currentUserId,
    currentUser,
  ])

  const deleteSession = useCallback(
    async (sessionId: string): Promise<boolean> => {
      try {
        if (!currentUserId || !currentBusinessId) return false
        await deleteSessionAPI(currentUserId, sessionId, currentBusinessId)
        setSessions((prev) => prev.filter((s) => s.id !== sessionId))
        trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_DELETED, {
          agent_session_id: sessionId,
        })
        return true
      } catch (caughtError) {
        setActionError(ActionError.FAILED_TO_DELETE_SESSION)
        return false
      }
    },
    [currentUserId, currentBusinessId],
  )

  const sendMessage = useCallback(
    async ({
      content,
      inputMethod = InputMethod.TYPING,
      promptMetadata,
      file,
      stateDelta,
    }: {
      content: string
      file?: { data: ArrayBuffer; name: string; type: string }
      inputMethod?: InputMethod
      promptMetadata?: Record<string, any>
      stateDelta?: Record<string, any>
    }) => {
      clearInput()
      await hookSendMessage({
        content,
        inputMethod,
        promptMetadata,
        chatMode,
        file,
        stateDelta: {
          ...stateDelta,
          business: {
            id: currentBusinessId,
            name: currentBusinessName,
            timezone: currentBusinessTimezone,
          },
          user: {
            id: currentUserId,
            email: currentUser?.email,
            firstName: currentUser?.firstName,
            lastName: currentUser?.lastName,
          },
        },
      })
    },
    [
      chatMode,
      hookSendMessage,
      currentBusinessTimezone,
      currentBusinessName,
      currentBusinessId,
      currentUserId,
      currentUser,
      clearInput,
    ],
  )

  const loadMostRecentSession = useCallback(async () => {
    if (!currentUserId || !currentBusinessId) return

    try {
      clearMessages()

      const userSessions = await getUserSessions(
        currentUserId,
        currentBusinessId,
      )
      setSessions(userSessions)

      if (userSessions.length > 0) {
        const sortedSessions = [...userSessions].sort(
          (a, b) => b.lastUpdateTime - a.lastUpdateTime,
        )
        await hookLoadSession(sortedSessions[0].id, currentBusinessId)
      }
    } catch (caughtError) {
      setActionError(ActionError.FAILED_TO_LOAD_SESSION)
    }
  }, [currentUserId, currentBusinessId, hookLoadSession, clearMessages])

  // Auto-load most recent session on mount (skip when first open of day so chat-open effect can create a new session)
  useEffect(() => {
    if (!isAgentChatEnabled || !currentUserId || !currentBusinessId || session)
      return
    if (
      isFirstOpenToday(
        currentUserId,
        currentBusinessId,
        currentBusinessTimezone ?? null,
      )
    )
      return
    loadMostRecentSession()
  }, [
    isAgentChatEnabled,
    currentUserId,
    currentBusinessId,
    currentBusinessTimezone,
    session,
    loadMostRecentSession,
  ])

  // When chat opens: first open of the day (per user/business, calendar day in business TZ)
  // starts a new session; otherwise ensure a session is loaded (if none from mount).
  useEffect(() => {
    if (
      !isAgentChatEnabled ||
      !currentUserId ||
      !currentBusinessId ||
      !isChatOpen
    )
      return

    const firstOpenOfDay = isFirstOpenToday(
      currentUserId,
      currentBusinessId,
      currentBusinessTimezone ?? null,
    )

    if (firstOpenOfDay && !session) {
      const stateDelta = {
        business: {
          id: currentBusinessId,
          name: currentBusinessName,
          timezone: currentBusinessTimezone,
        },
        user: {
          id: currentUserId,
          email: currentUser?.email,
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
        },
      }
      hookStartSession(stateDelta)
        .then(() => {
          markFirstOpenDone(
            currentUserId,
            currentBusinessId,
            currentBusinessTimezone ?? null,
          )
          const isFirstSession = sessions ? sessions.length === 0 : true
          trackEvent(TrackingEvent.AI_ASSISTANT_SESSION_CREATED, {
            is_first_session: isFirstSession,
            total_sessions: sessions ? sessions.length + 1 : 1,
            chat_mode: chatModeRef.current,
            is_auto_created: true,
          })
        })
        .catch(() => {
          setActionError(ActionError.FAILED_TO_CREATE_SESSION)
        })
    } else if (!session) {
      loadMostRecentSession()
    }
  }, [
    isAgentChatEnabled,
    currentUserId,
    currentBusinessId,
    currentBusinessTimezone,
    currentBusinessName,
    currentUser,
    sessions,
    isChatOpen,
    session,
    loadMostRecentSession,
    hookStartSession,
  ])

  useEffect(() => {
    if (isChatOpen && isAgentChatEnabled && chatMode === ChatMode.RAIL) {
      document.body.classList.add('agent-chat-rail-open')
    } else {
      document.body.classList.remove('agent-chat-rail-open')
    }

    return () => {
      document.body.classList.remove('agent-chat-rail-open')
    }
  }, [isChatOpen, isAgentChatEnabled, chatMode])

  // Switch to RAIL mode when a modal opens while chat is open
  useEffect(() => {
    if (isModalOpen && isChatOpen && chatMode !== ChatMode.RAIL) {
      setChatMode(ChatMode.RAIL)
    }
  }, [isModalOpen, isChatOpen])

  // Handle business switching
  useEffect(() => {
    const previousBusinessId = previousBusinessIdRef.current
    const hasBusinessChanged =
      previousBusinessId !== undefined &&
      currentBusinessId !== undefined &&
      previousBusinessId !== currentBusinessId

    previousBusinessIdRef.current = currentBusinessId

    if (hasBusinessChanged && currentBusinessId) {
      if (isChatOpen) {
        setIsChatOpen(false)
        setSessionOpenedAt(null)
        trackEvent(TrackingEvent.AI_ASSISTANT_CLOSED, {
          chat_mode: chatModeRef.current,
          agent_session_id: sessionIdRef.current,
          session_window_open_duration_ms:
            sessionOpenedAtRef.current !== null
              ? Date.now() - sessionOpenedAtRef.current
              : null,
        })
      }
      setSessions([])
      setActionError(null)
      clearMessages()
      clearInput()
    }
  }, [
    currentBusinessId,
    isChatOpen,
    currentUserId,
    isAgentChatEnabled,
    clearMessages,
    clearInput,
  ])

  const value = useMemo<AgentChatContextType>(
    () => ({
      isChatOpen,
      openChat,
      closeChat,
      chatMode,
      setChatMode: setChatModeWithRef,
      toggleChatMode,
      floatingPosition,
      updateFloatingPosition,
      floatingSize,
      updateFloatingSize,
      messages,
      isLoading,
      isLoadingSessions,
      isRespondingInAnotherTab,
      connectionState,
      sendMessage,
      startSession: hookStartSession,
      clearMessages,
      error,
      actionError,
      dismissActionError,
      session,
      sessions,
      streamingFinalStarted,
      streamingIntermediateText,
      loadSessions,
      selectSession,
      createNewSession,
      deleteSession,
      isResponding,
      inputState,
      setInputState,
      clearInput,
    }),
    [
      isChatOpen,
      openChat,
      closeChat,
      chatMode,
      setChatModeWithRef,
      toggleChatMode,
      floatingPosition,
      updateFloatingPosition,
      floatingSize,
      updateFloatingSize,
      messages,
      isLoading,
      isLoadingSessions,
      isRespondingInAnotherTab,
      connectionState,
      sendMessage,
      hookStartSession,
      clearMessages,
      error,
      actionError,
      dismissActionError,
      session,
      sessions,
      streamingFinalStarted,
      streamingIntermediateText,
      loadSessions,
      selectSession,
      createNewSession,
      deleteSession,
      isResponding,
      inputState,
      setInputState,
      clearInput,
    ],
  )

  const stateValue = useMemo<AgentChatStateContextType>(
    () => ({
      isChatOpen,
      chatMode,
    }),
    [isChatOpen, chatMode],
  )

  return (
    <AgentChatContext.Provider value={value}>
      <AgentChatStateContext.Provider value={stateValue}>
        {children}
      </AgentChatStateContext.Provider>
    </AgentChatContext.Provider>
  )
}

export function useAgentChat() {
  const context = useContext(AgentChatContext)
  if (context === undefined) {
    throw new Error('useAgentChat must be used within an AgentChatProvider')
  }
  return context
}

export function useAgentChatState() {
  const context = useContext(AgentChatStateContext)
  if (context === undefined) {
    throw new Error(
      'useAgentChatState must be used within an AgentChatProvider',
    )
  }
  return context
}
